import {
  AUTH,
  USER_CREATED,
  CREATE_USER,
  DELETE_USER,
  END_LOADING,
  FETCH_USERS,
  FETCH_USER_SINGLE,
  START_LOADING,
  UPDATE_USER,
  USER_LOGIN_FAIL,
  FETCH_CLIENT_BY_SEARCH,
} from "../constants/actionTypes.js";
import * as api from "../api/index.js";
import { toast } from "react-toastify";
import { debounce } from "lodash";
import { resolveLandingRoute } from "../utilities/landingRoute.js";
import {
  STAFF_2FA_PREAUTH_STORAGE_KEY,
  PARTNER_2FA_PREAUTH_STORAGE_KEY,
} from "../constants/twoFALogin.js";

/**
 * Keep the refresh token that sign-in now returns.
 *
 * The 401 interceptor in api/index.js has always known how to exchange one:
 * on TOKEN_EXPIRED it reads `localStorage.refreshToken`, calls
 * /user/refresh-token, stores the rotated pair and retries the request. It just
 * never had one to read, because the login response did not include a refresh
 * token — so the interceptor's very first step threw "No refresh token stored"
 * and it fell straight through to forcing a re-login.
 *
 * Storing it here is the whole of the missing half. Written under the same key
 * the interceptor already writes on rotation, so there is one key, not two.
 */
const persistRefreshToken = (data) => {
  try {
    if (data?.refreshToken) {
      localStorage.setItem("refreshToken", data.refreshToken);
    }
  } catch (_storageErr) {
    // Private mode or a full quota. Not fatal: without a stored refresh token
    // the session simply ends at the access token's expiry, which is the
    // behaviour that shipped before this line existed.
  }
};

export const signin = (formData, router) => async (dispatch) => {
  const debouncedSignin = debounce(async () => {
    try {
      dispatch({ type: START_LOADING });

      // Try regular user login first
      const response = await api.signIn(formData);
      const { data, status } = response;

      // ── 2FA required for privileged staff accounts ────────────────────────
      if (data.requiresTwoFA && data.preAuthToken) {
        try {
          sessionStorage.setItem(STAFF_2FA_PREAUTH_STORAGE_KEY, data.preAuthToken);
        } catch (_) {
          /* quota / private mode — fall back to encoded URL */
          router.push(
            `/auth/staff-2fa?token=${encodeURIComponent(data.preAuthToken)}`
          );
          return;
        }
        router.push("/auth/staff-2fa");
        return;
      }

      dispatch({ type: AUTH, data });
      persistRefreshToken(data);

      // ── Country context seed (multi_national) ──────────────────────────────
      // The user's home country now rides on data.result.country and is
      // persisted to localStorage.profile by the AUTH reducer, so CountryProvider
      // seeds homeCountry from it automatically — no landing change is needed
      // (the backend scopes data by x-country). We only clear any stale
      // `activeCountry` key left by a PREVIOUS session so the next session
      // starts on its own home country; the provider re-seeds it when absent.
      // Non-supers are locked to homeCountry regardless, so this is harmless.
      try {
        localStorage.removeItem("activeCountry");
      } catch (_) {
        /* private mode / quota — provider still defaults to home country */
      }

      if (status !== 200) {
        toast.success("Login Success!");
      }

      // Successful authentication replaces the login history entry. Back
      // should lead to the page visited before login, never to the login form.
      router.replace(resolveLandingRoute(data.result));
    } catch (error) {
      // console.log("Regular login failed, trying partner login...");

      // Try partner login as fallback
      try {
        const partnerResponse = await api.partnerSignIn(formData);

        if (partnerResponse.data.success) {
          // ── 2FA required ─────────────────────────────────────────────
          if (partnerResponse.data.requiresTwoFA && partnerResponse.data.preAuthToken) {
            try {
              sessionStorage.setItem(
                PARTNER_2FA_PREAUTH_STORAGE_KEY,
                partnerResponse.data.preAuthToken
              );
            } catch (_) {
              router.push(
                `/auth/partner-2fa?token=${encodeURIComponent(
                  partnerResponse.data.preAuthToken
                )}`
              );
              return;
            }
            router.push("/auth/partner-2fa");
            return;
          }

          // ── Normal login ─────────────────────────────────────────────
          const { token, partner } = partnerResponse.data.data;

          const partnerProfile = {
            result: {
              _id: partner._id,
              email: partner.email,
              companyName: partner.companyName,
              operationMode: "3PL_PARTNER",
              role: "3PL_PARTNER",
              partnerId: partner._id,
              imageUrl: partner.logo?.url || null,  // used by sidebar avatar
              logo: partner.logo || null,
            },
            token,
          };

          dispatch({ type: AUTH, data: partnerProfile });
          toast.success(`Welcome, ${partner.companyName}!`);
          router.replace(resolveLandingRoute(partnerProfile.result));
        }
      } catch (partnerError) {
        // Try 3PL staff login as final fallback
        try {
          const staffResponse = await api.staffSignIn(formData);

          if (staffResponse.data.success) {
            const { token, staff, partner, permissions } = staffResponse.data.data;

            // Create profile object compatible with existing structure
            const staffProfile = {
              result: {
                _id: staff._id,
                staffId: staff._id,
                email: staff.email,
                firstName: staff.firstName,
                lastName: staff.lastName,
                fullName: `${staff.firstName} ${staff.lastName}`,
                role: staff.role || 'Staff',
                operationMode: "3PL_STAFF",
                partnerId: partner._id,
                partnerName: partner.companyName,
                permissions: permissions,
              },
              token,
            };

            dispatch({ type: AUTH, data: staffProfile });
            toast.success(`Welcome, ${staff.firstName}!`);

            router.replace(resolveLandingRoute(staffProfile.result));
          }
        } catch (staffError) {
          // All logins failed — pick the most specific, truthful error.
          //
          // IMPORTANT: the /user/signin endpoint is the PRIMARY namespace for
          // dashboard users (admins, brand owners, sellers, healthcare, R&W,
          // restaurants). Partner/staff endpoints are only meaningful for
          // 3PL partners and their staff. If /user/signin returned a
          // specific error (404 "User doesn't exist", 400 "Invalid
          // credentials", 401 "PASSWORD_NOT_SET", 409 namespace conflict,
          // 403 deactivated, etc.), THAT is the authoritative message —
          // never override it with a 3PL "not found" which is expected
          // noise for a non-3PL user.
          const userStatus = error.response?.status;
          const userMsg = error.response?.data?.message;
          const userCode = error.response?.data?.code;
          const partnerMsg = partnerError.response?.data?.error?.message;
          const staffMsg = staffError.response?.data?.error?.message;

          const USER_ENDPOINT_SPECIFIC_STATUSES = [400, 401, 403, 409];
          const userHasSpecificError =
            userMsg && USER_ENDPOINT_SPECIFIC_STATUSES.includes(userStatus);

          let displayMessage;
          if (userHasSpecificError) {
            // The user's account was found but something else is wrong
            // (bad password, pending verification, namespace conflict,
            // missing password setup). Always surface this verbatim.
            displayMessage = userMsg;
          } else if (
            partnerMsg &&
            partnerMsg !== 'Partner not found' &&
            partnerMsg !== 'No account found with that email address.'
          ) {
            displayMessage = partnerMsg;
          } else if (
            staffMsg &&
            staffMsg !== 'Staff member not found' &&
            staffMsg !== 'No account found with that email address.'
          ) {
            displayMessage = staffMsg;
          } else if (userStatus === 404) {
            // Genuinely no account anywhere.
            displayMessage = 'No account found with that email address.';
          } else {
            displayMessage =
              userMsg ||
              partnerMsg ||
              staffMsg ||
              'Login failed. Please check your email and password.';
          }

          dispatch({
            type: USER_LOGIN_FAIL,
            payload: displayMessage,
          });
          // Provide a contextual action hint when we know the account exists
          // but password setup was never completed.
          if (userCode === 'PASSWORD_NOT_SET') {
            toast.error(displayMessage, { autoClose: 8000 });
          } else {
            toast.error(displayMessage);
          }
        }
      }
    } finally {
      dispatch({ type: END_LOADING });
    }
  }, 300);
  debouncedSignin();
};

export const signup = (formData, router) => async (dispatch) => {
  try {
    const { data } = await api.signUp(formData);

    dispatch({ type: AUTH, data });
    toast.success("Account Created");

    router.push("/");
    // window.location.reload(true); // Reloads the page
  } catch (error) {
    console.log(error);
    dispatch({
      type: USER_LOGIN_FAIL,
      payload:
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message,
    });
    toast.error(error.response.data.message);
  }
};

// Create Client Profile
export const createClientProfile = (post, userId) => async (dispatch) => {
  dispatch({ type: START_LOADING });
  try {
    const { data } = await api.createClientProfileApi(post, userId);

    dispatch({ type: CREATE_USER, payload: data });
    dispatch({ type: END_LOADING });
    toast.success("Profile Created");
    return Promise.resolve(); // For managing error in modal
  } catch (error) {
    console.log(error);
    toast.error(error.response.data.message);
    return Promise.reject(error); // For managing error in modal
  }
};

// Create Staff Profile
export const createStaffProfile = (post, userId) => async (dispatch) => {
  dispatch({ type: START_LOADING });
  try {
    const { data } = await api.createStaffProfileApi(post, userId);

    dispatch({ type: CREATE_USER, payload: data });
    dispatch({ type: USER_CREATED });

    if (data?.inviteEmailSent) {
      toast.success(
        `Account created — sign-in details emailed to ${post?.email || "them"}`
      );
    } else {
      toast.success(data?.reactivated ? "Account reactivated" : "Account created");
      toast.warn(
        data?.inviteEmailIssue ||
          "Invite email could not be sent — use 'Email Reset Link' on the RBAC page so they can set a password.",
        { autoClose: 9000 }
      );
    }

    dispatch({ type: END_LOADING });
    return data;
  } catch (error) {
    console.log(error);
    // ALWAYS clear the loading flag — leaving it set froze the Create button
    // on errors like "User already Exists" until a manual refresh.
    dispatch({ type: END_LOADING });
    // 409 = the address belongs to somebody. The form renders that inline, with
    // the actions the server offered, so a toast here would be the SECOND copy
    // of the same sentence — which is exactly what "User already Exists"
    // appearing twice was. Everything else still gets one toast, from here.
    if (error?.response?.status !== 409) {
      toast.error(error?.response?.data?.message || "Failed to create account");
    }
    throw error;
  }
};

//Rewrite this
// `options.includeOffboarded` asks the server for staff who have been removed
// from the directory. The server grants it to super users only and silently
// ignores it for everyone else, so this is safe to pass from any screen.
export const getUsers = (userId, role, options = {}) => async (dispatch) => {
  try {
    dispatch({ type: START_LOADING });
    const { data } = await api.fetchUsers(userId, role, options);

    dispatch({ type: FETCH_USERS, payload: { data } });
    dispatch({ type: END_LOADING });
  } catch (error) {
    console.log(error.response.data);
    toast.error(error.response.data);
    dispatch({ type: END_LOADING });
  }
};

export const getMobileUsers = (userId, role) => async (dispatch) => {
  try {
    dispatch({ type: START_LOADING });

    const { data } = await api.fetchMobileUsers(userId, role);
    // console.log(data)

    dispatch({ type: FETCH_USERS, payload: { data } });
    dispatch({ type: END_LOADING });
  } catch (error) {
    console.log(error);
    dispatch({ type: END_LOADING });
  }
};

export const getUser = (editedUserId, staff_id, role) => async (dispatch) => {
  try {
    dispatch({ type: START_LOADING });

    const { data } = await api.fetchUser(editedUserId, staff_id, role);

    dispatch({ type: FETCH_USER_SINGLE, payload: { agent: data } });
    dispatch({ type: END_LOADING });
  } catch (error) {
    console.log(error.message);
    toast.error(error.message);
    dispatch({ type: END_LOADING });
  }
};

export const getMobileUser =
  (id, userId, role, history) => async (dispatch) => {
    try {
      dispatch({ type: START_LOADING });

      const { data } = await api.fetchMobileUser(id, userId, role, history);

      dispatch({ type: FETCH_USER_SINGLE, payload: { agent: data } });
      dispatch({ type: END_LOADING });
    } catch (error) {
      console.log(error);
      toast.error(error);
    }
  };

export const profileUpdate =
  (userId, dataLoad, agentId) => async (dispatch) => {
    // console.log(userId, "userId", role, "role", agentId, "agentId");
    try {
      // if (role == "ADMIN") {
      // dispatch({ type: START_LOADING });
      const { data } = await api.updateUserRole(userId, dataLoad, agentId);

      dispatch({ type: UPDATE_USER, payload: data });

      // router.push('/admin/users');
      // } else {
      //   toast.error("Only Admins can Update Users!");
      // }
      dispatch({ type: END_LOADING });
      toast.success("Profile Updated");
      return data;
    } catch (error) {
      const message = error?.response?.data?.message || "Failed to update profile";
      toast.error(message);
      throw error;
    }
  };

export const deleteMobileUser = (client_id, userId) => async (dispatch) => {
  try {
    const { data } = await api.apiDeleteMobileUser(client_id, userId);

    dispatch({ type: DELETE_USER, payload: client_id });
    toast.success(data.message);
  } catch (error) {
    console.log(error);
    toast.error(error.response.data.message);
  }
};
// Suspend / reinstate / offboard / restore a staff member. One call for all
// four, because they are one decision with four directions and they must be
// audited identically. `action` is "SUSPEND" | "REINSTATE" | "OFFBOARD" |
// "RESTORE"; `reason` is required and is stored against the acting super user.
// Resolves true on success so the caller can close its dialog and refresh.
export const changeStaffLifecycle =
  (staff_id, userId, { action, reason }) =>
  async (dispatch) => {
    try {
      const { data } = await api.apiStaffLifecycle(staff_id, userId, { action, reason });
      // An offboarded staff member leaves the default listing; the others stay
      // in it with a new status, so only this one drops the row locally.
      if (action === "OFFBOARD") dispatch({ type: DELETE_USER, payload: staff_id });
      toast.success(data?.message || "Staff access updated.");
      return true;
    } catch (error) {
      console.log(error);
      toast.error(
        error?.response?.data?.message || "Could not change this staff member's access.",
      );
      return false;
    }
  };

export const getClientsBySearch = (searchQuery) => async (dispatch) => {
  try {
    dispatch({ type: START_LOADING });
    const {
      data: { data },
    } = await api.fetchClientsBySearch(searchQuery);

    dispatch({ type: FETCH_CLIENT_BY_SEARCH, payload: { data } });
    dispatch({ type: END_LOADING });
  } catch (error) {
    console.log(error);
    toast.error(error);
  }
};
